import { useMemo } from 'react'
import { useWidget } from '../../../../../providers/widget'
import { ExploreWidgetConfig } from '../../../config'
import { useExplorer } from '../../../providers'
import { ExploreProperty, priceFieldForMode, useFetchProperties, withBrowsePriceField } from '../../../data'
import { buildPriceBuckets } from './priceHistogram'
import { boundsFiltering } from "../../map/initialView"

/**
 * Price distribution behind the WHAT popover's slider (#870 slice 5).
 *
 * Sourced from the properties already loaded for the current search, with the
 * PRICE filter stripped from the query: a histogram drawn from price-filtered
 * results would collapse into the selected range the moment the visitor
 * narrows it, and there would be nothing left to dim. Everything else the
 * visitor has chosen (destination, dates, type, amenities, map bounds) stays
 * in, so the bars describe the set the slider actually cuts.
 *
 * With no price filter applied the stripped filter set hashes identically to
 * the grid's own query key, so the common case reads the grid's cache — every
 * page it has loaded included — and fetches nothing extra.
 */
export function usePriceDistribution(min: number, max: number): number[] {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state } = useExplorer()

  const boundsString = boundsFiltering(state) && state.mapBounds
    ? `${state.mapBounds.west},${state.mapBounds.south},${state.mapBounds.east},${state.mapBounds.north}`
    : undefined

  const filters = useMemo(() => {
    const next = { ...state.visitorFilters }
    delete next.price_min
    delete next.price_max
    if (boundsString) next.map_bounds = boundsString
    return withBrowsePriceField(next, options)
  }, [state.visitorFilters, boundsString, options])

  const { data } = useFetchProperties(feedId, filters, options.filter.limit, options.filter?.platform)

  // Strictly the column the price filter cuts on — a property whose materialized
  // price for that column is missing is left OUT rather than backfilled from
  // another column, so the bars can never promise a cut the filter cannot make.
  const priceField = priceFieldForMode(options.priceDisplay.noDates)
  return useMemo(() => {
    const rows: ExploreProperty[] = data?.pages?.flatMap((page) => page.result ?? []) ?? []
    const prices = rows
      .map((p) => (priceField === 'lowest' ? p.lowestPrice : priceField === 'average' ? p.averagePrice : p.price))
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    return buildPriceBuckets(prices, min, max)
  }, [data, priceField, min, max])
}
